import { useQuery } from '@tanstack/react-query';
import { X, User, Check, XCircle, Clock, Car, MessageSquare } from 'lucide-react';
import { ridesApi } from '@/lib/api';
import { RideRequest, RideRequestsPanelProps } from '@/types';
import { Button } from '@/components/ui/button';

type RequestListItem = RideRequest & {
  rideLabel?: string;
};

export const RideRequestsPanel = ({
  isOpen,
  onClose,
  rideId,
  onRespond,
  onMessageRider,
  respondingRequestId = null,
}: RideRequestsPanelProps) => {
  // Fetch all ride requests when "all" is selected, or specific ride requests
  const { data: requests = [], isLoading, refetch } = useQuery({
    queryKey: ['rideRequests', rideId],
    queryFn: async () => {
      const rides = await ridesApi.getMyRides();

      if (!rideId || rideId === 'all') {
        const allRequests: RequestListItem[] = [];

        for (const ride of rides) {
          const rideRequests = Array.isArray(ride.requests) && ride.requests.length > 0
            ? ride.requests
            : await ridesApi.getRideRequests(ride.id);

          allRequests.push(
            ...rideRequests.map((request) => ({
              ...request,
              rideLabel: `${ride.destinationName} • ${ride.date} • ${ride.startTime}`,
            }))
          );
        }
        return allRequests
          .filter((request) => request.status === 'pending')
          .sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
      }

      const selectedRide = rides.find((ride) => ride.id === rideId);
      const rideRequests = Array.isArray(selectedRide?.requests) && selectedRide.requests.length > 0
        ? selectedRide.requests
        : await ridesApi.getRideRequests(rideId);

      return rideRequests.sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
    },
    enabled: isOpen && !!rideId,
  });

  const isAllView = rideId === 'all';

  const handleRespond = (requestRideId: string, requestId: string, action: 'accept' | 'decline') => {
    onRespond(requestRideId, requestId, action);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative w-full max-w-md mx-4 bg-background rounded-2xl shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <h2 className="text-lg font-bold">Ride Requests</h2>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Car className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">{isAllView ? 'No pending requests across your rides' : 'No pending requests'}</p>
              <p className="text-sm text-muted-foreground/60 mt-1">Riders will appear here when they request to join</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request: RequestListItem) => (
                <RequestCard 
                  key={request.id} 
                  request={request} 
                  onRespond={handleRespond}
                  onMessageRider={onMessageRider}
                  isResponding={respondingRequestId === request.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Individual request card component
const RequestCard = ({ 
  request, 
  onRespond,
  onMessageRider,
  isResponding,
}: { 
  request: RequestListItem; 
  onRespond: (rideId: string, requestId: string, action: 'accept' | 'decline') => void;
  onMessageRider?: (riderId: string, rideId: string) => void;
  isResponding?: boolean;
}) => {
  const isPending = request.status === 'pending';
  const isAccepted = request.status === 'accepted';

  return (
    <div className={`bg-card rounded-xl p-4 border ${
      isPending ? 'border-primary/20' : isAccepted ? 'border-green-500/30' : 'border-red-500/30'
    }`}>
      {/* Rider Info */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
          {request.rider.avatar ? (
            <img 
              src={request.rider.avatar} 
              alt={request.rider.name}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <User className="w-6 h-6 text-primary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{request.rider.name}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-muted-foreground">
              {request.rider.ridesCompleted ?? 0} rides
            </span>
            {request.rider.rating && (
              <>
                <span className="text-xs text-muted-foreground">•</span>
                <span className="text-xs text-yellow-600">⭐ {request.rider.rating.toFixed(1)}</span>
              </>
            )}
          </div>
          {request.rider.major && (
            <p className="text-xs text-muted-foreground mt-1">{request.rider.major}</p>
          )}
        </div>
        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
          isPending ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
          isAccepted ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
          'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        }`}>
          {isPending ? 'Pending' : isAccepted ? 'Accepted' : 'Rejected'}
        </div>
      </div>

      {/* Request Message */}
      {request.rideLabel && (
        <div className="bg-primary/5 rounded-lg p-3 mb-3">
          <p className="text-xs font-medium text-primary">Ride</p>
          <p className="text-sm text-muted-foreground">{request.rideLabel}</p>
        </div>
      )}

      {request.message && (
        <div className="bg-muted/50 rounded-lg p-3 mb-3">
          <p className="text-sm text-muted-foreground">"{request.message}"</p>
        </div>
      )}

      {/* Request Time */}
      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
        <Clock className="w-3 h-3" />
        <span>Requested {new Date(request.requestedAt).toLocaleDateString()} at {new Date(request.requestedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
      </div>

      {/* Action Buttons */}
      {isPending && (
        <div className="space-y-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onMessageRider?.(request.rider.id, request.rideId)}
            className="w-full h-10 rounded-lg"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Message Rider First
          </Button>
          <div className="flex gap-2">
          <Button
            type="button"
            onClick={() => onRespond(request.rideId, request.id, 'accept')}
            disabled={isResponding}
            className="flex-1 h-10 bg-green-600 hover:bg-green-700 text-white rounded-lg"
          >
            <Check className="w-4 h-4 mr-1" />
            Accept
          </Button>
          <Button
            type="button"
            onClick={() => onRespond(request.rideId, request.id, 'decline')}
            disabled={isResponding}
            variant="outline"
            className="flex-1 h-10 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-lg"
          >
            <XCircle className="w-4 h-4 mr-1" />
            Decline
          </Button>
          </div>
        </div>
      )}

      {isAccepted && onMessageRider && (
        <Button
          type="button"
          variant="secondary"
          onClick={() => onMessageRider(request.rider.id, request.rideId)}
          className="w-full h-10 rounded-lg"
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          Continue Chat
        </Button>
      )}
    </div>
  );
};

export default RideRequestsPanel;
